import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";

import FaIcon from "~/components/FaIcon";

import BaseLink from "~/components/links/BaseLink";

import ContentContainer from "~/components/layout/ContentContainer";

import useStringTranslation from "~/hooks/translations/useStringTranslation";

import transition from "~/utilities/transition";
import { screenReaderText } from "~/styles/screenReaderText";

const OuterWrapper = styled.nav`
    padding: 15px 0;
    background-color: ${({ backgroundColor, theme }) =>
        backgroundColor === "white"
            ? theme.palette.white
            : theme.palette.grayThree};
    margin-bottom: ${({ noMarginBottom, theme }) =>
        noMarginBottom ? `0` : `calc(${theme.spacing.small} / 2)`};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        margin-bottom: ${({ noMarginBottom, theme }) =>
            noMarginBottom ? `0` : theme.spacing.small};
    }
`;

const Wrapper = styled.ul`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-size: 1.3rem;
    margin: -10px -7.5px 0;
`;

const BreadcrumbWrapper = styled.li`
    padding: 10px 7.5px 0;
`;

const Icon = styled(FaIcon)`
    font-size: 1rem;
    margin-left: 15px;
`;

const Link = styled(BaseLink)`
    ${transition({}, "color")};
    color: ${({ theme }) => theme.palette.purple};
    text-transform: capitalize;
    text-decoration: underline;

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.palette.teal};
    }
`;

const Last = styled.span`
    color: ${({ theme }) => theme.palette.black};
    text-transform: capitalize;
`;

const HiddenNavHeadingLabel = styled.h2`
    ${screenReaderText}
`;

const Breadcrumbs = ({
    backgroundColor = "white",
    crumbs,
    noMarginBottom = false,
    lastCrumbLabel = "",
}) => {
    const { breadcrumbText, breadcrumbNavigationText } = useStringTranslation([
        { text: "Breadcrumb", handle: "breadcrumbText" },
        { text: "Breadcrumb Navigation", handle: "breadcrumbNavigationText" },
    ]);

    return (
        <OuterWrapper
            {...{ backgroundColor, noMarginBottom }}
            aria-label={breadcrumbText}
        >
            <HiddenNavHeadingLabel id="breadcrumbNavLabel">
                {breadcrumbNavigationText}
            </HiddenNavHeadingLabel>
            <ContentContainer>
                <Wrapper aria-labelledby="breadcrumbNavLabel">
                    {crumbs &&
                        crumbs.map(({ crumbLabel, pathname }, index) =>
                            index < crumbs.length - 1 ? (
                                <BreadcrumbWrapper key={crumbLabel}>
                                    <Link to={pathname}>{crumbLabel}</Link>
                                    <Icon name="chevron-right" />
                                </BreadcrumbWrapper>
                            ) : (
                                <BreadcrumbWrapper
                                    key={
                                        lastCrumbLabel
                                            ? lastCrumbLabel
                                            : crumbs[crumbs.length - 1]
                                                  .crumbLabel
                                    }
                                    aria-current="page"
                                >
                                    <Last>
                                        {lastCrumbLabel
                                            ? lastCrumbLabel
                                            : crumbs[crumbs.length - 1]
                                                  .crumbLabel}
                                    </Last>
                                </BreadcrumbWrapper>
                            ),
                        )}
                </Wrapper>
            </ContentContainer>
        </OuterWrapper>
    );
};

Breadcrumbs.propTypes = {
    backgroundColor: PropTypes.oneOf(["white", "gray"]),
    crumbs: PropTypes.arrayOf(
        PropTypes.shape({
            crumbLabel: PropTypes.string.isRequired,
            pathname: PropTypes.string,
        }).isRequired,
    ).isRequired,
    noMarginBottom: PropTypes.bool,
    lastCrumbLabel: PropTypes.string,
};

export default Breadcrumbs;
